import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useFileJobRecovery } from '@/hooks/useFileJobRecovery'
import { useCreateZipJob } from '@/hooks/useFileJobs'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { downloadZipResult, mintIdempotencyKey } from '@/services/file-job.service'

/**
 * Everything Library needs to turn a selection into a ZIP and to show the work
 * already in flight — kept beside the page rather than in `hooks/` because
 * nothing else has a Library selection to package.
 *
 * The idempotency key doubles as the confirmation's open state: it is minted
 * once when the dialog opens, so a retried `Confirm` on that dialog is a replay
 * of one command, while dismissing and reopening mints a new one.
 *
 * @param {ReadonlySet<string>} selectedFileIds - The complete current selection.
 */
export function useLibraryZip(selectedFileIds: ReadonlySet<string>) {
	const queryClient = useQueryClient()
	const zipJob = useCreateZipJob()
	const recovery = useFileJobRecovery()
	const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null)
	// Session-only "how many Files went into this request", for one enrichment
	// line. It is gone after a refresh, and every row still reads correctly then.
	const [selectedCounts, setSelectedCounts] = useState<ReadonlyMap<string, number>>(new Map())

	const open = (): void => {
		try {
			const key = mintIdempotencyKey()
			// Clears the previous attempt's error, so a reopened dialog does not open
			// showing a failure the user already saw and dismissed.
			zipJob.reset()
			setIdempotencyKey(key)
		} catch (error) {
			notification.error(apiErrorMessage(error, 'A ZIP cannot be prepared in this browser context.'))
		}
	}

	const cancel = (): void => {
		zipJob.reset()
		setIdempotencyKey(null)
	}

	const confirm = (): void => {
		if (!idempotencyKey) return
		const fileIds = [...selectedFileIds]
		zipJob.mutate(
			{ fileIds, idempotencyKey },
			{
				// Only on acceptance: a rejected attempt leaves the dialog open with its
				// error, the selection intact, and nothing recorded anywhere.
				onSuccess: (created) => {
					setIdempotencyKey(null)
					setSelectedCounts((previous) => new Map(previous).set(created.requestId, fileIds.length))
				},
			},
		)
	}

	return {
		rows: recovery.rows,
		isLoadingRows: recovery.isLoading,
		selectedCounts,
		isConfirmOpen: idempotencyKey !== null,
		isSubmitting: zipJob.isPending,
		errorMessage: zipJob.error ? apiErrorMessage(zipJob.error) : null,
		open,
		cancel,
		confirm,
		download: (requestId: string) => downloadZipResult(queryClient, requestId),
	}
}
